import { isControlMessage } from './types';
/** Default interval between application-level pings (ms). Override via AcpTransportOptions. */
const DEFAULT_HEARTBEAT_INTERVAL_MS = 30_000;
/** Default pong response deadline (ms). If no pong arrives within this time, the connection is considered dead. */
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 10_000;
/**
 * Create an ACP WebSocket transport connected to the VM Agent.
 *
 * Supports both the positional argument signature (backward compat) and
 * the options object signature. Prefer the options object for new code.
 */
export function createAcpWebSocketTransport(wsOrOptions, onAgentStatus, onAcpMessage, onClose, onError, onLifecycleEvent) {
    // Normalize to options object.
    // Duck-type check: if wsOrOptions has addEventListener it's a WebSocket
    // (positional args form), otherwise it's an options object.
    let opts;
    if ('addEventListener' in wsOrOptions && typeof wsOrOptions.addEventListener === 'function') {
        opts = {
            ws: wsOrOptions,
            onAgentStatus: onAgentStatus,
            onAcpMessage: onAcpMessage,
            onClose,
            onError,
            onLifecycleEvent,
        };
    }
    else {
        opts = wsOrOptions;
    }
    const { ws } = opts;
    // --- Application-level heartbeat ---
    // Sends JSON {"type":"ping"} and expects {"type":"pong"} back.
    // Works through any proxy (Cloudflare, etc.) because these are regular
    // data frames, not WebSocket protocol-level control frames.
    const heartbeatInterval = opts.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
    const heartbeatTimeout = opts.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS;
    let heartbeatTimer = null;
    let pongTimer = null;
    let waitingForPong = false;
    function startHeartbeat() {
        if (heartbeatInterval <= 0)
            return;
        stopHeartbeat();
        heartbeatTimer = setInterval(() => {
            if (ws.readyState !== WebSocket.OPEN)
                return;
            waitingForPong = true;
            ws.send(JSON.stringify({ type: 'ping' }));
            // Start pong deadline timer
            pongTimer = setTimeout(() => {
                if (!waitingForPong)
                    return;
                opts.onLifecycleEvent?.({
                    source: 'acp-transport',
                    level: 'warn',
                    message: 'Heartbeat pong timeout — closing connection to trigger reconnect',
                    context: { heartbeatTimeoutMs: heartbeatTimeout },
                });
                // Force-close to trigger the onClose → reconnect path
                ws.close(4000, 'heartbeat_timeout');
            }, heartbeatTimeout);
        }, heartbeatInterval);
    }
    function stopHeartbeat() {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
        if (pongTimer) {
            clearTimeout(pongTimer);
            pongTimer = null;
        }
        waitingForPong = false;
    }
    function handlePong() {
        waitingForPong = false;
        if (pongTimer) {
            clearTimeout(pongTimer);
            pongTimer = null;
        }
    }
    ws.addEventListener('message', (event) => {
        try {
            const data = JSON.parse(event.data);
            if (isControlMessage(data)) {
                switch (data.type) {
                    case 'agent_status':
                        opts.onAgentStatus(data);
                        break;
                    case 'agent_crash_report':
                        if (opts.onAgentCrashReport) {
                            opts.onAgentCrashReport(data);
                        }
                        else {
                            opts.onAcpMessage(data);
                        }
                        break;
                    case 'session_state':
                        opts.onSessionState?.(data);
                        break;
                    case 'session_replay_complete':
                        opts.onSessionReplayComplete?.();
                        break;
                    case 'session_prompting':
                        opts.onSessionPrompting?.(true);
                        break;
                    case 'session_prompt_done':
                        opts.onSessionPrompting?.(false);
                        break;
                    case 'pong':
                        handlePong();
                        break;
                    case 'ping':
                        // Server shouldn't send pings to client, but respond if it does
                        if (ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ type: 'pong' }));
                        }
                        break;
                    default:
                        break;
                }
            }
            else {
                opts.onAcpMessage(data);
            }
        }
        catch {
            opts.onLifecycleEvent?.({
                source: 'acp-transport',
                level: 'warn',
                message: 'Failed to parse WebSocket message as JSON',
                context: {
                    dataLength: typeof event.data === 'string' ? event.data.length : 0,
                    preview: typeof event.data === 'string' ? event.data.slice(0, 200) : 'non-string',
                },
            });
        }
    });
    // Start heartbeat once connection is open
    ws.addEventListener('open', () => {
        startHeartbeat();
    });
    // If already open (e.g. passed after open event), start immediately
    if (ws.readyState === WebSocket.OPEN) {
        startHeartbeat();
    }
    const wrappedOnClose = (event) => {
        stopHeartbeat();
        opts.onClose?.(event.code, event.reason);
    };
    ws.addEventListener('close', wrappedOnClose);
    if (opts.onError) {
        ws.addEventListener('error', opts.onError);
    }
    return {
        sendAcpMessage(message) {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify(message));
            }
            else {
                opts.onLifecycleEvent?.({
                    source: 'acp-transport',
                    level: 'warn',
                    message: 'Send failed: WebSocket not open',
                    context: { readyState: ws.readyState, messageType: 'acp' },
                });
            }
        },
        sendSelectAgent(agentType) {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'select_agent', agentType }));
            }
            else {
                opts.onLifecycleEvent?.({
                    source: 'acp-transport',
                    level: 'warn',
                    message: 'Send failed: WebSocket not open',
                    context: { readyState: ws.readyState, messageType: 'select_agent', agentType },
                });
            }
        },
        close() {
            stopHeartbeat();
            ws.close();
        },
        get connected() {
            return ws.readyState === WebSocket.OPEN;
        },
    };
}
//# sourceMappingURL=websocket.js.map